/**
 * Abysius OpenAI-Compatible API Service
 *
 * This service integrates with Abysius AI API for:
 * - Chat completions (streaming & non-streaming)
 * - Image generation (DALL-E)
 * - Text generation
 */

// Types for OpenAI-compatible API
export interface ChatMessage {
  role: "system" | "user" | "assistant";
  content: string | ChatContentPart[];
}

export interface ChatContentPart {
  type: "text" | "image_url";
  text?: string;
  image_url?: {
    url: string;
    detail?: "auto" | "low" | "high";
  };
}

export interface ChatCompletionRequest {
  model: string;
  messages: ChatMessage[];
  stream?: boolean;
  temperature?: number;
  max_tokens?: number;
  top_p?: number;
  frequency_penalty?: number;
  presence_penalty?: number;
  stop?: string | string[];
  user?: string;
}

export interface ChatCompletionChoice {
  index: number;
  message: {
    role: "assistant";
    content: string;
  };
  finish_reason: "stop" | "length" | "content_filter" | null;
}

export interface ChatCompletionResponse {
  id: string;
  object: "chat.completion";
  created: number;
  model: string;
  choices: ChatCompletionChoice[];
  usage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
}

export interface ChatCompletionChunk {
  id: string;
  object: "chat.completion.chunk";
  created: number;
  model: string;
  choices: {
    index: number;
    delta: {
      role?: "assistant";
      content?: string;
    };
    finish_reason: "stop" | "length" | "content_filter" | null;
  }[];
}

export interface ImageGenerationRequest {
  prompt: string;
  model?: "dall-e-2" | "dall-e-3";
  n?: number;
  size?: "256x256" | "512x512" | "1024x1024" | "1792x1024" | "1024x1792";
  quality?: "standard" | "hd";
  style?: "vivid" | "natural";
  response_format?: "url" | "b64_json";
}

export interface ImageGenerationResponse {
  created: number;
  data: {
    url?: string;
    b64_json?: string;
    revised_prompt?: string;
  }[];
}

export interface AbysiusModel {
  id: string;
  object: "model";
  created: number;
  owned_by: string;
}

export interface ModelsListResponse {
  object: "list";
  data: AbysiusModel[];
}

export interface AbysiusError {
  error: {
    message: string;
    type: string;
    param: string | null;
    code: string;
  };
}

// Default configuration
const DEFAULT_CONFIG = {
  baseUrl: import.meta.env.VITE_APP_ABYSIUS_API_URL || "https://dev.abysius.ai/api/chat/v1",
  // Pre-configured API key for Spryboard users
  apiKey: import.meta.env.VITE_APP_ABYSIUS_API_KEY ,
  defaultModel: "gpt-3.5-turbo",
  defaultImageModel: "dall-e-3",
  defaultTemperature: 0.7,
  defaultMaxTokens: 2048,
};

// Local storage key for API key persistence
const ABYSIUS_API_KEY_STORAGE_KEY = "spryboard-abysius-api-key";

class AbysiusApiService {
  private apiKey: string | null = null;
  private baseUrl: string;

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl || DEFAULT_CONFIG.baseUrl;
    // Initialize with pre-configured API key
    this.initializeApiKey();
  }

  private initializeApiKey(): void {
    // First check environment variable
    const envApiKey = import.meta.env.VITE_APP_ABYSIUS_API_KEY;
    if (envApiKey) {
      this.apiKey = envApiKey;
      return;
    }
    
    // Then check localStorage for user override
    try {
      const storedKey = localStorage.getItem(ABYSIUS_API_KEY_STORAGE_KEY);
      if (storedKey) {
        this.apiKey = storedKey;
        return;
      }
    } catch (e) {
      console.warn("Failed to load API key from localStorage:", e);
    }

    // Use default pre-configured API key
    this.apiKey = DEFAULT_CONFIG.apiKey;
  }

  /**
   * Set the API key for authentication
   */
  setApiKey(apiKey: string): void {
    this.apiKey = apiKey;
  }

  /**
   * Get the current API key
   */
  getApiKey(): string | null {
    return this.apiKey;
  }

  /**
   * Check if API key is configured
   */
  isConfigured(): boolean {
    return !!this.apiKey;
  }

  /**
   * Get authorization headers
   */
  private getHeaders(): HeadersInit {
    if (!this.apiKey) {
      throw new Error("Abysius API key not configured");
    }
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${this.apiKey}`,
    };
  }

  /**
   * Handle API errors
   */
  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorData = (await response.json().catch(() => ({
        error: {
          message: response.statusText,
          type: "api_error",
          code: "unknown_error",
        },
      }))) as AbysiusError;

      throw new Error(
        errorData.error?.message || `API Error: ${response.status}`,
      );
    }
    return response.json() as Promise<T>;
  }

  /**
   * List available models
   */
  async listModels(): Promise<ModelsListResponse> {
    const response = await fetch(`${this.baseUrl}/models`, {
      method: "GET",
      headers: this.getHeaders(),
    });
    return this.handleResponse<ModelsListResponse>(response);
  }

  /**
   * Create a chat completion (non-streaming)
   */
  async createChatCompletion(
    request: ChatCompletionRequest,
  ): Promise<ChatCompletionResponse> {
    const response = await fetch(`${this.baseUrl}/chat/completions`, {
      method: "POST",
      headers: this.getHeaders(),
      body: JSON.stringify({
        ...request,
        stream: false,
      }),
    });
    return this.handleResponse<ChatCompletionResponse>(response);
  }

  /**
   * Create a streaming chat completion
   */
  async *createChatCompletionStream(
    request: ChatCompletionRequest,
  ): AsyncGenerator<ChatCompletionChunk, void, unknown> {
    const response = await fetch(`${this.baseUrl}/chat/completions`, {
      method: "POST",
      headers: this.getHeaders(),
      body: JSON.stringify({
        ...request,
        stream: true,
      }),
    });

    if (!response.ok) {
      const errorData = (await response.json().catch(() => ({
        error: {
          message: response.statusText,
          type: "api_error",
          code: "unknown_error",
        },
      }))) as AbysiusError;
      throw new Error(
        errorData.error?.message || `API Error: ${response.status}`,
      );
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error("No response body");
    }

    const decoder = new TextDecoder();
    let buffer = "";

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          const trimmedLine = line.trim();
          if (!trimmedLine || !trimmedLine.startsWith("data: ")) {
            continue;
          }

          const data = trimmedLine.slice(6);
          if (data === "[DONE]") {
            return;
          }

          try {
            const chunk = JSON.parse(data) as ChatCompletionChunk;
            yield chunk;
          } catch {
            // Skip invalid JSON
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  }

  /**
   * Generate text from a prompt (simplified wrapper)
   */
  async generateText(
    prompt: string,
    options?: {
      model?: string;
      systemPrompt?: string;
      temperature?: number;
      maxTokens?: number;
    },
  ): Promise<string> {
    const messages: ChatMessage[] = [];

    if (options?.systemPrompt) {
      messages.push({
        role: "system",
        content: options.systemPrompt,
      });
    }

    messages.push({
      role: "user",
      content: prompt,
    });

    const response = await this.createChatCompletion({
      model: options?.model || DEFAULT_CONFIG.defaultModel,
      messages,
      temperature: options?.temperature ?? DEFAULT_CONFIG.defaultTemperature,
      max_tokens: options?.maxTokens ?? DEFAULT_CONFIG.defaultMaxTokens,
    });

    return response.choices[0]?.message?.content || "";
  }

  /**
   * Generate text with streaming (simplified wrapper)
   */
  async generateTextStream(
    prompt: string,
    onChunk: (text: string) => void,
    options?: {
      model?: string;
      systemPrompt?: string;
      temperature?: number;
      maxTokens?: number;
    },
  ): Promise<string> {
    const messages: ChatMessage[] = [];

    if (options?.systemPrompt) {
      messages.push({
        role: "system",
        content: options.systemPrompt,
      });
    }

    messages.push({
      role: "user",
      content: prompt,
    });

    let fullText = "";

    for await (const chunk of this.createChatCompletionStream({
      model: options?.model || DEFAULT_CONFIG.defaultModel,
      messages,
      temperature: options?.temperature ?? DEFAULT_CONFIG.defaultTemperature,
      max_tokens: options?.maxTokens ?? DEFAULT_CONFIG.defaultMaxTokens,
    })) {
      const content = chunk.choices[0]?.delta?.content || "";
      if (content) {
        fullText += content;
        onChunk(content);
      }
    }

    return fullText;
  }

  /**
   * Generate image using DALL-E
   */
  async generateImage(
    request: ImageGenerationRequest,
  ): Promise<ImageGenerationResponse> {
    const response = await fetch(`${this.baseUrl}/images/generations`, {
      method: "POST",
      headers: this.getHeaders(),
      body: JSON.stringify({
        model: request.model || DEFAULT_CONFIG.defaultImageModel,
        prompt: request.prompt,
        n: request.n || 1,
        size: request.size || "1024x1024",
        quality: request.quality || "standard",
        style: request.style || "vivid",
        response_format: request.response_format || "url",
      }),
    });
    return this.handleResponse<ImageGenerationResponse>(response);
  }

  /**
   * Generate image from prompt (simplified wrapper)
   */
  async generateImageFromPrompt(
    prompt: string,
    options?: {
      model?: "dall-e-2" | "dall-e-3";
      size?: "256x256" | "512x512" | "1024x1024" | "1792x1024" | "1024x1792";
      quality?: "standard" | "hd";
      style?: "vivid" | "natural";
      n?: number;
    },
  ): Promise<{ url?: string; b64_json?: string; revised_prompt?: string }[]> {
    const response = await this.generateImage({
      prompt,
      model: options?.model || "dall-e-3",
      size: options?.size || "1024x1024",
      quality: options?.quality || "standard",
      style: options?.style || "vivid",
      n: options?.n || 1,
      response_format: "b64_json", // Use base64 to avoid CORS issues
    });

    return response.data.map((item) => ({
      url: item.url,
      b64_json: item.b64_json,
      revised_prompt: item.revised_prompt,
    }));
  }

  /**
   * Analyze an image using vision-capable model
   */
  async analyzeImage(
    imageUrl: string,
    prompt: string,
    options?: {
      model?: string;
      detail?: "auto" | "low" | "high";
    },
  ): Promise<string> {
    const messages: ChatMessage[] = [
      {
        role: "user",
        content: [
          {
            type: "text",
            text: prompt,
          },
          {
            type: "image_url",
            image_url: {
              url: imageUrl,
              detail: options?.detail || "auto",
            },
          },
        ],
      },
    ];

    const response = await this.createChatCompletion({
      model: options?.model || "gpt-4-vision-preview",
      messages,
    });

    return response.choices[0]?.message?.content || "";
  }

  /**
   * Generate a Mermaid diagram from text description
   */
  async textToMermaid(prompt: string): Promise<string> {
    const systemPrompt = `You are an expert at creating Mermaid diagrams. 
Given a description, generate a valid Mermaid diagram syntax.
Only output the Mermaid code, no explanations or markdown code blocks.
Supported diagram types: flowchart, sequence, class, state, er, gantt, pie, mindmap.`;

    const response = await this.generateText(prompt, {
      model: "gpt-4",
      systemPrompt,
      temperature: 0.3,
    });

    // Clean up response - remove markdown code blocks if present
    return response
      .replace(/^```mermaid\n?/i, "")
      .replace(/^```\n?/, "")
      .replace(/\n?```$/, "")
      .trim();
  }

  /**
   * Generate code from diagram description
   */
  async diagramToCode(
    imageDataUrl: string,
    texts: string[],
    options?: {
      theme?: string;
      framework?: string;
    },
  ): Promise<string> {
    const prompt = `Analyze this wireframe/diagram and generate HTML/CSS code for it.
${texts.length > 0 ? `Text elements in the diagram: ${texts.join(", ")}` : ""}
${options?.framework ? `Use ${options.framework} framework.` : "Use vanilla HTML/CSS."}
${options?.theme === "dark" ? "Use a dark theme." : "Use a light theme."}
Generate clean, responsive code.`;

    return this.analyzeImage(imageDataUrl, prompt, {
      model: "gpt-4-vision-preview",
      detail: "high",
    });
  }
}

// Export singleton instance
export const abysiusApi = new AbysiusApiService();

// Export for custom instances
export { AbysiusApiService, DEFAULT_CONFIG as ABYSIUS_CONFIG };
